import React, { useState, useEffect } from 'react';
import { Card, Button, DataTable, Modal } from '../components/common/UIComponents';
import api from '../api/axios';

export default function Stations() {
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStation, setEditingStation] = useState(null);
  const [qrModalCode, setQrModalCode] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    points: 10,
    participation_points: 5,
    has_stages: false,
    code: '',
    sort_order: 0,
    status: 'active',
    stages: []
  });

  const fetchStations = async () => {
    try {
      const res = await api.get('/stations');
      setStations(res.data);
    } catch (err) {
      console.error('Error al cargar estaciones:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStations();
  }, []);

  const defaultStagesForCode = (baseCode) => [
    { stage_number: 1, name: 'Día 1 / Etapa 1', code: `${baseCode}-STAGE-1`, status: 'active' },
    { stage_number: 2, name: 'Día 2 / Etapa 2', code: `${baseCode}-STAGE-2`, status: 'active' },
    { stage_number: 3, name: 'Día 3 / Etapa 3', code: `${baseCode}-STAGE-3`, status: 'active' },
    { stage_number: 4, name: 'Día 4 / Etapa 4', code: `${baseCode}-STAGE-4`, status: 'active' }
  ];

  const handleOpenModal = (station = null) => {
    if (station) {
      setEditingStation(station);
      setFormData({
        name: station.name,
        description: station.description || '',
        points: station.points !== undefined && station.points !== null ? station.points : 0,
        participation_points: station.participation_points !== undefined && station.participation_points !== null ? station.participation_points : 5,
        has_stages: !!station.has_stages,
        code: station.code,
        sort_order: station.sort_order || 0,
        status: station.status || 'active',
        stages: station.stages && station.stages.length > 0 ? station.stages : defaultStagesForCode(station.code)
      });
    } else {
      setEditingStation(null);
      const randomCode = 'STATION-' + Math.floor(1000 + Math.random() * 9000);
      setFormData({
        name: '',
        description: '',
        points: 10,
        participation_points: 5,
        has_stages: false,
        code: randomCode,
        sort_order: stations.length + 1,
        status: 'active',
        stages: defaultStagesForCode(randomCode)
      });
    }
    setIsModalOpen(true);
  };

  const handleToggleStages = (checked) => {
    setFormData(prev => ({
      ...prev,
      has_stages: checked,
      stages: checked && prev.stages.length === 0 ? defaultStagesForCode(prev.code) : prev.stages
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editingStation) {
        await api.put(`/stations/${editingStation.id}`, formData);
      } else {
        await api.post('/stations', formData);
      }
      setIsModalOpen(false);
      fetchStations();
    } catch (err) {
      console.error('Error al guardar estación:', err);
      alert(err.response?.data?.message || 'Error al guardar la estación.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de que desea eliminar esta estación?')) return;
    try {
      await api.delete(`/stations/${id}`);
      fetchStations();
    } catch (err) {
      console.error('Error al eliminar estación:', err);
      alert(err.response?.data?.message || 'Error al eliminar la estación.');
    }
  };

  const columns = [
    { header: 'Orden', accessor: 'sort_order' },
    { header: 'Nombre Estación', accessor: 'name' },
    { header: 'Código', accessor: 'code' },
    { 
      header: 'Estructura de Puntos', 
      render: (row) => (
        <div className="text-xs">
          <span className="font-bold text-emerald-600 block">+{row.participation_points || 5} pts Participación</span>
          <span className="text-gray-500 block">+{row.points} pts Destreza/Por defecto</span>
        </div>
      ) 
    },
    {
      header: 'Etapas Multidía',
      render: (row) => (
        row.has_stages ? (
          <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-purple-100 text-purple-700 border border-purple-200">
            ⚡ {row.stages ? row.stages.length : 4} Etapas (Días 1-4)
          </span>
        ) : (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
            Estación Única
          </span>
        )
      )
    },
    {
      header: 'Estado',
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${row.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
          {row.status === 'active' ? 'Activa' : 'Inactiva'}
        </span>
      )
    },
    {
      header: 'Código QR',
      render: (row) => (
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            let qrStr = `station=${row.code}`;
            if (row.has_stages && row.stages && row.stages.length > 0) {
              qrStr += `|stage=${row.stages[0].code}`;
            }
            if (row.points && row.points > 0) {
              qrStr += `|destreza=${row.points}`;
            }
            setQrModalCode(qrStr);
          }}
        >
          📱 Ver QR
        </Button>
      )
    },
    {
      header: 'Acciones',
      render: (row) => (
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => handleOpenModal(row)}>
            Editar
          </Button>
          <Button size="sm" variant="danger" onClick={() => handleDelete(row.id)}>
            Eliminar
          </Button>
        </div>
      )
    }
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Estaciones y Etapas (4 Días)</h1>
          <p className="text-sm text-gray-500">Administre las estaciones del evento, los puntos fijos de participación (5 pts) y las etapas diarias asociadas.</p>
        </div>
        <Button onClick={() => handleOpenModal()}>
          + Registrar Nueva Estación
        </Button>
      </div>

      <Card>
        {loading ? (
          <div className="p-8 text-center text-gray-500">Cargando lista de estaciones...</div>
        ) : (
          <DataTable columns={columns} data={stations} emptyMessage="No hay estaciones registradas aún." />
        )}
      </Card>

      {/* Form Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingStation ? 'Editar Estación' : 'Registrar Nueva Estación'}
      >
        <form onSubmit={handleSave} className="space-y-5 max-h-[80vh] overflow-y-auto pr-2 custom-scrollbar">
          <div>
            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Nombre de la Estación</label>
            <input
              type="text"
              required
              className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 outline-none"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Ej: Estación Trivias A"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Descripción</label>
            <textarea
              className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 outline-none"
              rows="2"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Detalles o reglas de la estación..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Puntos Participación (Fijos)</label>
              <input
                type="number"
                min="0"
                required
                className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 outline-none font-bold text-emerald-600"
                value={formData.participation_points}
                onChange={(e) => setFormData({ ...formData, participation_points: parseInt(e.target.value, 10) || 5 })}
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Puntos Destreza (Por Defecto)</label>
              <input
                type="number"
                min="0"
                required
                className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 outline-none font-bold text-purple-600"
                value={formData.points}
                onChange={(e) => setFormData({ ...formData, points: e.target.value === '' ? 0 : parseInt(e.target.value, 10) })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Código QR Estación</label>
              <input
                type="text"
                required
                className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 outline-none font-mono"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Orden en Circuito</label>
              <input
                type="number"
                min="0"
                className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value, 10) })}
              />
            </div>
          </div>

          {/* Toggle Stages */}
          <div className="p-4 bg-purple-50 rounded-2xl border border-purple-100 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-xs font-black text-purple-900 uppercase">¿Configurar Etapas por Días (1 al 4)?</h4>
                <p className="text-[10px] text-purple-600">Permite registrar el puntaje día a día, bloqueando más de 1 intento por etapa.</p>
              </div>
              <input
                type="checkbox"
                className="w-5 h-5 accent-purple-600 cursor-pointer"
                checked={formData.has_stages}
                onChange={(e) => handleToggleStages(e.target.checked)}
              />
            </div>

            {formData.has_stages && (
              <div className="pt-3 border-t border-purple-200 space-y-3">
                <h5 className="text-[11px] font-black text-purple-800 uppercase">Etapas Configuradas:</h5>
                <div className="grid grid-cols-1 gap-2">
                  {formData.stages.map((stg, sIdx) => (
                    <div key={sIdx} className="flex gap-2 items-center bg-white p-2.5 rounded-xl border border-purple-100 shadow-sm">
                      <span className="w-6 h-6 rounded-full bg-purple-600 text-white font-black text-[10px] flex items-center justify-center">
                        {stg.stage_number}
                      </span>
                      <input
                        type="text"
                        className="flex-grow px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-semibold"
                        value={stg.name}
                        onChange={(e) => {
                          const updated = [...formData.stages];
                          updated[sIdx].name = e.target.value;
                          setFormData({ ...formData, stages: updated });
                        }}
                      />
                      <input
                        type="text"
                        className="w-36 px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-mono"
                        value={stg.code}
                        onChange={(e) => {
                          const updated = [...formData.stages];
                          updated[sIdx].code = e.target.value;
                          setFormData({ ...formData, stages: updated });
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-gray-100">
            <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit">
              Guardar Estación y Etapas
            </Button>
          </div>
        </form>
      </Modal>

      {/* QR Viewer Modal */}
      <Modal
        isOpen={!!qrModalCode}
        onClose={() => setQrModalCode(null)}
        title="Contenido del Código QR de Estación / Etapa"
      >
        <div className="text-center py-6 space-y-4">
          <p className="text-sm text-gray-600">Este es el formato exacto para escanear y registrar puntos:</p>
          <div className="bg-slate-900 text-emerald-400 p-4 rounded-2xl font-mono text-sm break-all shadow-inner">
            {qrModalCode}
          </div>
          <p className="text-xs text-gray-400">
            Formato: <code className="text-purple-600 font-bold">station=CODIGO|stage=ETAPA|destreza=X</code>
          </p>
          <div className="pt-2">
            <Button variant="secondary" onClick={() => navigator.clipboard.writeText(qrModalCode)}>
              📋 Copiar Formato QR
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

